import styled from '@emotion/styled';
import { breakpointQueries, breakpoints, colors, spacing } from '../../../utils/styleguide';
import Typography from '../text/Typography';
import { parseISO as parseISODate } from 'date-fns';
import { useMediaQuery } from '../../../utils/hooks/useMediaQuery';
import useLocale from '../../../utils/hooks/useLocale';
import { formatDate } from '../../../utils/helpers/date';
import GhostButton from '../buttons/GhostButton';
import IconImage from '../Image';
import Image from '../ImageNext';

const ContentUpdateCardContainer = styled.div<{ width: number }>`
  position: relative;
  height: 500px;
  justify-content: center;
  display: flex;
  flex: 0 0 auto;
  overflow-y: hidden;
  :hover {
    .overlay {
      opacity: 1;
    }
    .imageHover {
      filter: brightness(0.3);
    }
  }
  width: ${({ width }) => `${width * 100}%`};
  box-sizing: border-box;
  scroll-snap-align: start;
  padding-top: ${spacing[3]}px;
  padding-left: ${spacing[4]}px;

  ${breakpoints.tablet} {
    padding-left: ${spacing[5]}px;
  }
`;

const ContentWrapperLink = styled.a`
  position: relative;
  width: 100%;
  cursor: pointer;
`;

const ContentUpdateOverlay = styled.div<{ primaryColor?: string }>`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  height: 100%;
  width: 100%;
  opacity: 0;
  transition: 0.5s ease;
`;

const ContentUpdateInfo = styled.div`
  display: flex;
  flex-direction: column;
  position: absolute;
  padding: ${spacing[4]}px ${spacing[3]}px;
  top: 0;
  left: 0;
`;

const ContentUpdateCaption = styled.div`
  position: absolute;
  top: 25%;
  padding: 0 ${spacing[3]}px;
`;

const ContentUpdateFooter = styled.div`
  display: flex;
  position: absolute;
  flex-direction: column;
  bottom: 0;
  width: 100%;
  align-items: flex-start;
`;

const ImageWrapper = styled.div`
  background-color: ${colors.black};
  position: relative;
  display: flex;
  width: 100%;
  height: 100%;
  transition: 0.5s ease-in-out;
  filter: brightness(1);
`;

const StyledImage = styled(Image)`
  position: absolute;
  width: 100%;
  height: 100%;
  object-fit: cover;
  opacity: 0.7;
`;

const ButtonWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const ReadMoreButton = styled(GhostButton)`
  margin: ${spacing[3]}px 0;
  padding: ${spacing[1]}px;
  align-self: flex-end;
`;

const StyledIconImage = styled(IconImage)`
  margin: 0 0 ${spacing[3]}px ${spacing[3]}px;
  filter: brightness(0) saturate(100%) invert(100%) sepia(10%) saturate(7437%) hue-rotate(290deg)
    brightness(119%) contrast(100%);
  max-height: ${spacing[5]}px;
`;

const ContentUpdateCard = ({
  postId,
  initiativeTitle,
  width = 1,
  onClick,
  primaryColor,
  showLocation = true,
  location,
  publishedAt,
  imageSrc,
  imageAlt,
  caption,
  ctaLabel,
  iconSrc,
  iconAlt,
}: {
  postId: string;
  initiativeTitle?: string;
  width?: number;
  onClick?: (postId: string) => void;
  primaryColor?: string;
  showLocation?: boolean;
  location?: string;
  publishedAt?: string;
  imageSrc?: string;
  imageAlt: string;
  caption?: string | null;
  ctaLabel?: string;
  iconSrc?: string;
  iconAlt: string;
}) => {
  const [isTabletOrLarger] = useMediaQuery(breakpointQueries.tablet);

  return (
    <ContentUpdateCardContainer width={width}>
      <ContentWrapperLink onClick={() => onClick && onClick(postId)}>
        <ImageWrapper className="imageHover">
          {imageSrc && <StyledImage src={imageSrc} alt={imageAlt} width={800} height={800} />}
        </ImageWrapper>

        <ContentUpdateMetrics
          publishedAt={publishedAt}
          initiativeTitle={initiativeTitle}
          initiativeLocation={showLocation ? location : undefined}
        />
        {isTabletOrLarger && (
          <ContentUpdateOverlay className="overlay" primaryColor={primaryColor}>
            <ContentUpdateMetrics
              publishedAt={publishedAt}
              initiativeTitle={initiativeTitle}
              initiativeLocation={showLocation ? location : undefined}
            />
            <ContentUpdateCaption>
              <Typography maxNumberOfLines={11} variant="body" color={colors.white}>
                {caption}
              </Typography>
              <ButtonWrapper>
                <ReadMoreButton
                  color={colors.whitePrimary}
                  textVariant="buttonSmall"
                  trailingIconName="chevron-right"
                  label={ctaLabel || 'read more'}
                  onClick={() => onClick && onClick(postId)}
                  hideUnderline
                />
              </ButtonWrapper>
            </ContentUpdateCaption>

            <ContentUpdateFooter>
              {iconSrc && <StyledIconImage url={iconSrc} alt={iconAlt} />}
            </ContentUpdateFooter>
          </ContentUpdateOverlay>
        )}
      </ContentWrapperLink>
    </ContentUpdateCardContainer>
  );
};

const ContentUpdateMetrics = ({
  publishedAt,
  initiativeTitle,
  initiativeLocation,
}: {
  publishedAt?: string;
  initiativeTitle?: string;
  initiativeLocation?: string;
}) => {
  const pageLocaleString = useLocale();

  return (
    <ContentUpdateInfo>
      <Typography variant="caption" color={colors.white}>
        {publishedAt
          ? formatDate({
              date: parseISODate(publishedAt.toString()),
              locale: pageLocaleString,
              options: { dateStyle: 'long' },
            })
          : ''}
      </Typography>
      <Typography variant="overlineSmall" color={colors.white}>
        {initiativeTitle}
      </Typography>
      {initiativeLocation && (
        <Typography variant="overlineSmall" color={colors.white}>
          {initiativeLocation}
        </Typography>
      )}
    </ContentUpdateInfo>
  );
};

export default ContentUpdateCard;
